import * as LDClient from 'launchdarkly-js-client-sdk';
import { LDFlagChangeset, LDFlagSet } from 'launchdarkly-js-client-sdk';
import { isEqual, merge } from 'lodash-es';

import { PromiseReturnType } from '@rbi-ctg/frontend';
import { loadLanguage, loadRegion } from 'state/intl/hook';
import { getCurrentVersion } from 'utils/appflow';
import { ApiKeys, Service, appVersionCode, platform, sanityDataset } from 'utils/environment';
import { OTPAuthDeliveryMethod } from 'utils/otp';

const LAUNCH_DARKLY_API_KEY: string = ApiKeys.get(Service.launchDarkly);

const createAnonymousUserAttributes = async (deviceId?: string) => {
  const version = await getCurrentVersion();
  const anon = {
    anonymous: true,
    custom: {
      host: window.location.host,
      platform: platform(),
      device_id: deviceId || '',
      userClient: window.navigator.userAgent || '',
      appVersion: version ? version.binaryVersionCode : appVersionCode(),
      appFlowBuildId: version ? version.buildId : '',
      language: loadLanguage(),
      sanityDataset: `${sanityDataset}_${loadRegion().toLowerCase()}`
    },
    country: loadRegion()
  };
  return normalizeUserAttributes(anon);
};

/**
 * The following enum and utility types give us strongly-typed
 * LaunchDarkly flag values. When adding a new flag, two steps
 * must be taken to ensure the correct type is associated with the flag
 */

/**
 * First update this enum to include the flag name
 * Note: the flag name should appear exactly as is it is in the
 * Launch Darkly dashboard
 */
export enum LaunchDarklyFlag {
  AUTO_FILL_TEST_CARD = 'autoFillTestCard',
  CURBSIDE_VEHICLE_DESCRIPTION_MAX_LENGTH = 'curbsideVehicleDescriptionMaxLength',
  ENABLE_BOTTOM_SERVICE_MODE = 'enableBottomServiceMode',
  ENABLE_CATERING = 'enableCatering',
  ENABLE_CUSTOM_BIRTHDAY_INPUT = 'enableCustomBirthdayInput',
  ENABLE_DELIVERY = 'delivery',
  ENABLE_CURBSIDE = 'enableCurbside',
  ENABLE_CUSTOMER_CAN_EDIT_OWN_PHONE_NUMBER = 'enable-customer-can-edit-own-phone-number',
  ENABLE_AUTO_SIGN_IN_ON_SIGN_UP = 'enableAutoSignInOnSignUp',
  ENABLE_CATERING_DELIVERY = 'enableCateringDelivery',
  ENABLE_IM_CLOSE_DISPLAY = 'enableImCloseDisplay',
  ENABLE_DELIVERY_PUSH_NOTIFICATION = 'enableDeliveryPushNotification',
  ENABLE_DELIVERY_WATCH_DRIVER_LOCATION = 'enableDeliveryWatchDriverLocation',
  ENABLE_DIAGNOSTIC_TOOLS = 'enableDiagnosticTools',
  ENABLE_INTERNAL_DIAGNOSTIC_TOOLS = 'enableInternalDiagnosticTools',
  ENABLE_MENU_UPSELL_ITEMS = 'enable-menu-upsell-items',
  ENABLE_MODIFIED_TAKEOUT = 'enableModifiedTakeout',
  ENABLE_NEW_SERVICE_MODE_FLOW = 'enableNewServiceModeFlow',
  ENABLE_SERVICE_MODE_CART_SELECTION = 'enableServiceModeCartSelection',
  ENABLE_FREE_DELIVERY_CHECKOUT_PROGRESS_BAR = 'enableFreeDeliveryCheckoutProgressBar',
  ENABLE_OFFERS = 'enableOffers',
  ENABLE_OFFERS_MOBILE = 'enableOffersMobile',
  ENABLE_OFFERS_ACTIVATION = 'enableOffersActivation',
  ENABLE_OFFERS_SELECT_RESTAURANT_CTA = 'enableOffersSelectRestaurantCta',
  ENABLE_MORE_INFO_OPTIONAL_OFFER_DISCLAIMER = 'enableMoreInfoOptionalOfferDisclaimer',
  ENABLE_ACCOUNT_IN_FOOTER = 'enable-account-in-footer',
  ENABLE_NATIVE_MOBILE_ACCOUNT = 'enable-native-mobile-account',
  ENABLE_ONE_TIME_PASSWORD = 'enable-one-time-password',
  ENABLE_ORDER_LINK_NAV = 'enableOrderLinkNav',
  ENABLE_REMOVE_OFFER_BUTTON_ON_CHECKOUT_SCREEN = 'enableRemoveOfferButtonOnCheckoutScreen',
  ENABLE_RULESETS_VALIDATION = 'enableRulesetsValidation',
  ENABLE_ORDER_TIMED_FIRE = 'enableOrderTimedFire',
  ENABLE_ORDER_TIMED_FIRE_CARROLS = 'enableOrderTimedFireCarrols',
  ENABLE_ORDER_TIMED_FIRE_NCR = 'enableOrderTimedFireNCR',
  ENABLE_ORDER_TIMED_FIRE_SICOM = 'enableOrderTimedFireSICOM',
  ENABLE_PROMO_CODE_OFFERS = 'enablePromoCodeOffers',
  ENABLE_QUERY_OFFERS_FROM_BACKEND = 'enable-query-offers-from-backend',
  FIRE_ORDER_AHEAD = 'fireOrderAhead',
  SHOW_ORDERING_ISSUE = 'show-ordering-issue-info',
  DATADOG_LOG_SAMPLE_RATE = 'datadogLogSampleRate',
  DATADOG_RUM_SAMPLE_RATE = 'datadogRumSampleRate',
  SHOW_SITE_UPDATING = 'showSiteUpdating',
  OVERRIDE_CHECKOUT_LIMIT = 'CheckoutLimitOverrideCents',
  OVERRIDE_CHECKOUT_CATERING_LIMIT = 'CheckoutCateringLimitOverrideCents',
  OVERRIDE_CHECKOUT_DELIVERY_MINIMUM = 'checkoutDeliveryMinimumOverrideCents',
  OVERRIDE_CHECKOUT_CATERING_MINIMUM = 'checkoutCateringMinimumOverrideCents',
  DISABLE_DRIVE_THRU = 'disableDriveThru',
  DISABLE_TAKE_OUT = 'disableTakeOut',
  DISABLE_DINE_IN = 'disableDineIn',
  DISABLE_KIOSK_CASH_CHECKOUT = 'disableKioskCashCheckout',
  DISABLE_LOCALIZATION = 'disableLocalization',
  ENABLE_LOGROCKET = 'log-rocket',
  ENABLE_REORDER = 'enable-reorder',
  ENABLE_GEOLOCATION_ASK_IN_NEARBY_TAB_EMPTY_STORE_LOCATOR = 'enableGeolocationAskInNearbyTabEmptyStoreLocator',
  ENABLE_MAP_VIEW_STORE_TABS = 'enableMapViewStoreTabs',
  ENABLE_SANITY_CONTROLLED_MOBILE_NAV = 'enableSanityControlledMobileNav',
  ENABLE_STORE_LOCATOR_LIST_VIEW = 'enableStoreLocatorListView',
  ENABLE_STORE_LOCATOR_DELIVERY_TAB = 'enableStoreLocatorDeliveryTab',
  ENABLE_STORE_LOCATOR_RECENT_TAB = 'enableStoreLocatorRecentTab',
  ENABLE_FAVORITE_STORES = 'enableFavoriteStores',
  ENABLE_FAVORITE_STORES_DEFAULT_VIEW_IF_USER_IS_LOADED = 'enableFavoriteStoresDefaultViewIfUserIsLoaded',
  ENABLE_CHECKOUT_UPSELL_ITEMS_2 = 'enable-checkout-upsell-items-2',
  ENABLE_APPLE_PAY = 'enableApplePay',
  ENABLE_GOOGLE_PAY = 'enableGooglePay',
  ENABLE_TH_DIRECT_PAY = 'th-direct-pay',
  ENABLE_TH_DIRECT_PAY_APPLE_PAY = 'enableThDirectPayApplePay',
  ENABLE_TH_DIRECT_PAY_FOR_DELIVERY = 'enable-th-direct-pay-for-delivery',
  ENABLE_TH_DIRECT_PAY_GOOGLE_PAY = 'enableThDirectPayGooglePay',
  ENABLE_AUTO_SHOW_REGION_SELECTOR = 'enable-auto-show-region-selector',
  ENABLE_COOKIE_VERSIONING = 'enableCookieVersioning',
  ENABLE_EU_NUTRITION_DISPLAY = 'nutritionEuDisplay',
  STORE_DIAMETER_FOR_RESTAURANT_LIST_VIEW = 'storeDiameterForRestaurantListView',
  ENABLE_ADD_GOOGLE_PAY_BUTTON = 'enableAddToGooglePay',
  ENABLE_ADD_APPLE_WALLET_BUTTON = 'enableAddToAppleWallet',
  ENABLE_LEGACY_EMAIL_IMPORT = 'enableLegacyEmailImport',
  ENABLE_NUTRITION_EXPLORER_ALLERGENS = 'enable-nutrition-explorer-allergens',
  ENABLE_NUTRITION_EXPLORER = 'enableNutritionExplorer',
  ENABLE_NO_PIN_CHECK_PREPAID_MERGE = 'enable-no-pin-check-prepaid-merge',
  ENABLE_CLEAR_STORE_AFTER_ORDER = 'enable-clear-store-after-order',
  ENABLE_FORMATTED_CALORIES_FOR_DEFAULT = 'enableFormattedCaloriesForDefault',
  ENABLE_GIFT_CARD = 'gift-card-payment-method',
  ENABLE_LOYALTY_BARCODE = 'loyalty-bar-code',
  ENABLE_GIFT_CARD_SCAN = 'gift-card-scan',
  ENABLE_GIFT_CARD_SWIPE = 'gift-card-swipe',
  DISPLAY_NUTRITION_WITH_MODIFIERS_FROM_SANITY = 'displayNutritionWithModifiersFromSanity',
  ENABLE_NUTRITION_EXPLORER_TASTY_SWAPS_LANDING = 'enableNutritionExplorerTastySwapsLanding',
  ENABLE_IDENTITY_SYNC_ON_LOGIN = 'enable-identity-sync-on-login',
  ENABLE_LOYALTY_2_0_SEE_MORE_OPTIONS = 'enable-see-more-options-loyalty-20',
  ENABLE_LOYALTY = 'enable-loyalty-fe',
  ENABLE_CLAIM_LOYALTY_POINTS = 'enable-claim-loyalty-points',
  ENABLE_LOYALTY_REWARD_TOGGLE = 'enable-loyalty-reward-toggle',
  ENABLE_LOYALTY_REWARDS_NAV_LINK = 'enable-loyalty-rewards-nav-link',
  ENABLE_NUTRITION_DECIMALS = 'enableNutritionDecimals',
  ENABLE_RECENTLY_ORDERED_ITEMS = 'enableRecentlyOrderedItems',
  ENABLE_RUTR = 'enableRutr',
  ENABLE_RUTR_HOME_PAGE_BANNER = 'enableRutrHomePageBanner',
  ENABLE_RUTR_STATIC_LANDING_PAGE = 'rutr-static-landing-page',
  ENABLE_APP_FLOW_UPDATE = 'enable-app-flow-update',
  ENABLE_SIMPLE_DELIVERY_FORM = 'enable-simple-delivery-form',
  TWO_STEP_CHECKOUT = 'two-step-checkout',
  ENABLE_OFFER_GIFTING = 'enable-offer-gifting',
  ENABLE_OFFERS_REFRESH = 'enableOffersRefresh',
  ENABLE_OFFER_COUNT_BADGE = 'enableOfferCountBadge',
  LOYALTY_CARD_DETAILS_POLL_INTERVAL_MS = 'loyaltyCardDetailsPollIntervalMs',
  MINIMUM_APPFLOW_BUILD_ID = 'minimumAppflowBuildId',
  ENABLE_LOYALTY_20_ON_BOARDING_CONTENT_CARD_ON_THE_HOME_PAGE = 'enableLoyalty20OnBoardingContentCardOnTheHomePage',
  ENABLE_SANITY_CDN = 'enableSanityCdn',
  ENABLE_STORE_CONFIRMATION_MODAL = 'enableStoreConfirmationModal',
  ORDER_PRICE_REFRESH_INTERVAL_MS = 'orderPriceRefreshIntervalMs',
  ENABLE_TRENDING = 'enable-trending',
  ENABLE_HOMEPAGE_ALERT = 'enableHomepageAlert',
  DELIVERY_RADIUS_IN_MILES = 'deliveryRadiusInMiles',
  ENABLE_OFFER_BADGES = 'enableOfferBadges',
  DELIVERY_BANNER_POLLING = 'deliveryBannerPolling',
  STORE_AVAILABILITY_POLLING = 'storeAvailabilityPolling',
  REFRESH_RESTAURANT_AVAILABILITY = 'refreshRestaurantAvailability',
  ENABLE_MAP_VIEW_MOBILE_VERTICAL_SCROLL_AND_SLIM_STORE_CARDS = 'enableMapViewMobileVerticalScrollAndSlimStoreCards',
  ENABLE_LOVED_ONES_DELIVERY_ADDRESS_TEXT = 'enableYourLovedOnesDeliveryAddressText',
  ENABLE_COOKIE_BANNER = 'enableCookieBanner',
  ENABLE_ORDERING = 'enableOrdering',
  SUPPORT_BACKLOG_MSG = 'supportBacklogMsg',
  USE_HIGH_VOLUME_SUPPORT_MSG = 'use-high-volume-support-msg',
  ENABLE_INTERNATIONAL_SIGN_UP = 'enableInternationalSignUp',
  ENABLE_OTP_EMAIL = 'enableOtpEmail',
  RESET_CART_RESET_STORE_TIMEOUT = 'resetStoreResetCartTimeout',
  ENABLE_SIDE_BY_SIDE_HERO_BUTTONS = 'enable-side-by-side-hero-buttons',
  TIP_PERCENT_THRESHOLD_CENTS = 'tipPercentThresholdCents',
  ENABLE_SANITY_FOOTER = 'enableSanityFooter',
  ENABLE_SANITY_WEB_NAVIGATION = 'enableSanityWebNavigation',
  ENABLE_RECENT_ITEMS_ADD_TO_CART = 'enable-recent-items-add-to-cart',
  ENABLE_USER_SAVED_DELIVERY_ADDRESS_PHONE = 'enableUserSavedDeliveryAddressPhone',
  CHEF_CART_UPSELL_ITEM_COUNT = 'chefUpsellItemCount',
  ENABLE_REQUEST_MY_INFORMATION = 'enableRequestMyInformation',
  ENABLE_ACCOUNT_DELETE_PAGE = 'enableAccountDeletePage',
  ENABLE_EDIT_RECENT_ITEM = 'enable-edit-recent-item',
  ENABLE_FEATURE_HOME_PAGE = 'enableFeatureHomePage',
  ENABLE_HOME_PAGE_APP_DATA_MIGRATION = 'enableHomePageAppDataMigration',
  ENABLE_ORDER_SUPPORT = 'enableOrderSupport',
  MAX_CUSTOMER_INITIATED_REFUND_PRICE_CENTS = 'maxCustomerInitiatedRefundPriceCents',
  MAX_CUSTOMER_INITIATED_PARTIAL_REFUND_COUNT = 'maxCustomerInitiatedPartialRefundCount',
  MAX_CUSTOMER_INITIATED_FULL_REFUND_COUNT = 'maxCustomerInitiatedFullRefundCount',
  ENABLE_CIR_DELIVERY = 'enableCirDelivery',
  ENABLE_CIR_DRIVE_THRU = 'enableCirDriveThu',
  ENABLE_CIR_CURBSIDE = 'enableCirCurbside',
  ENABLE_CIR_TAKEOUT = 'enableCirTakeout',
  ENABLE_AUTO_LOCALE = 'enableAutoLocale',
  ENABLE_AUTO_SELECT_RESTAURANT = 'enableAutoSelectRestaurant',
  ENABLE_HOME_PAGE_RECENT_ITEMS = 'enableHomePageRecentItems',
  MOBILE_CART_UPSELL_LOCATION = 'mobileCartUpsellLocation',
  ENABLE_COGNITO_SIGNUP_IN_BE = 'enableCognitoSignupInBe',
  ENABLE_NEW_PHONE_NUMBER_FIELD = 'enableNewPhoneNumberField',
  ENABLE_SIGN_UP_BIRTHDAY_FIELD = 'enableSignUpBirthdayField',
  ENABLE_SIGN_UP_PHONE_FIELD = 'enableSignUpPhoneField',
  ENABLE_DELIVERY_CANCELLATION_POLICY = 'enableDeliveryCancellationPolicy',
  ENABLE_SCAN_BUTTONS_HOMEPAGE = 'enableScanButtonsHomepage',
  ENABLE_STATIC_MENU = 'enableStaticMenu',
  ENABLE_IN_APP_QUIZ_GAME = 'enableInAppQuizGame',
  ENABLE_HTML_IN_APP_MESSAGES_IN_BRAZE_SDK = 'enable-html-in-app-messages-in-braze-sdk',
  ENABLE_RADAR_TRACKING = 'enableRadarTrackingOnNewSessions',
  ENABLE_OLD_OFFERS_HOMEPAGE_SECTION = 'enableOldOffersHomepageSection',
  ENABLE_COMMUNICATION_PREFERENCES = 'enableCommunicationPreferences',
  ENABLE_ALLERGENS_DISCLAIMER = 'enableAllergensDisclaimer',
  ENABLE_GUEST_AND_MULTI_STEP_SUPPORT_PAGE = 'enableGuestAndMultiStepSupportForm',
  ENABLE_RECENT_ITEMS_WITH_MODIFIERS = 'enableRecentItemsWithModifiers',
  ENABLE_ZERO_BOUNCE_ERROR_MODAL = 'enable-zero-bounce-error-modal',
  ENABLE_TH_VOUCHER_CODES = 'enableThVoucherCodes',
  DEFAULT_SERVICE_MODE_IN_STORE = 'defaultServiceModeInStore',
  ENABLE_PREPAID_CARD_AUTO_RELOAD = 'enable-prepaid-card-auto-reload',
  ENABLE_POPEYES_REWARDS = 'enable-popeyes-rewards',
  HEARTBEAT_INTERVAL_MAX_MINUTES = 'heartbeat-interval-max-minutes',
  CHEF_RECOMMENDATION_ENGINE_2 = 'chef-recommendation-engine-2',
  DISCOUNTED_DELIVERY_THRESHOLD_CENTS = 'discounted-delivery-threshold-cents',
  TIERED_CATERING_DELIVERY_FEES = 'tiered-catering-delivery-fees',
  TIERED_DELIVERY_FEES = 'tiered-delivery-fees',
  ENABLE_LOCKED_OFFERS = 'enableLockedOffers',
  ENABLE_HIDE_ADD_NEW_PAYMENT_FIELDS = 'enableHideAddNewPaymentFields',
  ENABLE_SIGN_UP = 'enableSignUp',
  HIDE_SERVICE_MODE = 'hideServiceMode',
  ENABLE_FEATURE_NUTRITION_INTEGRATION = 'enable-feature-nutrition-integration',
  FORCE_HOMEPAGE_AUTH = 'force-homepage-auth',
  HALLOWEEN_BK_PROMO_LOCATIONS = 'halloween-bk-promo-locations',
  SEND_POSTAL_CODE_ONLY_FOR_FIRST_DATA_PAYMENT = 'send-postal-code-only-for-first-data-payment',
  ENABLE_STORE_SELECTION_2_0 = 'enable-store-selection-2',
  SINGLE_REGION_LANGUAGE_SELECTOR = 'singleRegionLanguageSelector',
  // temporary flag: once store selection 2.0 is integrated, this can be removed.
  DISPLAY_SELECTED_RESTAURANT_INFORMATION_WHEN_RESTAURANT_UNAVAILABLE = 'display-selected-restaurant-information-when-restaurant-unavailable',

  // ****** TEST FLAGS ******
  // These flags may not exist in LD but allows us to enable their functionality
  // by using ?rbiFeature.$FLAG=true on the url such as rbiFeature.enable-test-google-pay-web=true
  ENABLE_TEST_APPLE_PAY_WEB = 'enable-test-apple-pay-web',
  ENABLE_TEST_GOOGLE_PAY_WEB = 'enable-test-google-pay-web'
}

/**
 * Secondly, update the appropriate union based on the type
 * your new flag should return
 */
export type NumericFlags =
  | LaunchDarklyFlag.CURBSIDE_VEHICLE_DESCRIPTION_MAX_LENGTH
  | LaunchDarklyFlag.FIRE_ORDER_AHEAD
  | LaunchDarklyFlag.OVERRIDE_CHECKOUT_LIMIT
  | LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM
  | LaunchDarklyFlag.OVERRIDE_CHECKOUT_CATERING_LIMIT
  | LaunchDarklyFlag.OVERRIDE_CHECKOUT_CATERING_MINIMUM
  | LaunchDarklyFlag.MINIMUM_APPFLOW_BUILD_ID
  | LaunchDarklyFlag.DELIVERY_RADIUS_IN_MILES
  | LaunchDarklyFlag.DELIVERY_BANNER_POLLING
  | LaunchDarklyFlag.STORE_AVAILABILITY_POLLING
  | LaunchDarklyFlag.DATADOG_RUM_SAMPLE_RATE
  | LaunchDarklyFlag.RESET_CART_RESET_STORE_TIMEOUT
  | LaunchDarklyFlag.TIP_PERCENT_THRESHOLD_CENTS
  | LaunchDarklyFlag.CHEF_CART_UPSELL_ITEM_COUNT
  | LaunchDarklyFlag.DISCOUNTED_DELIVERY_THRESHOLD_CENTS
  | LaunchDarklyFlag.MAX_CUSTOMER_INITIATED_REFUND_PRICE_CENTS
  | LaunchDarklyFlag.MAX_CUSTOMER_INITIATED_PARTIAL_REFUND_COUNT
  | LaunchDarklyFlag.MAX_CUSTOMER_INITIATED_FULL_REFUND_COUNT
  | LaunchDarklyFlag.STORE_DIAMETER_FOR_RESTAURANT_LIST_VIEW
  | LaunchDarklyFlag.DATADOG_LOG_SAMPLE_RATE;

export type StringFlags =
  | LaunchDarklyFlag.MOBILE_CART_UPSELL_LOCATION
  | LaunchDarklyFlag.DEFAULT_SERVICE_MODE_IN_STORE;

export type EnumFlags = LaunchDarklyFlag.ENABLE_ONE_TIME_PASSWORD;

/**
 * Special case - for enum flag types, update this conditional type to
 * associate a flag name with a specific enum
 */
export type EnumFlagTypes<F extends EnumFlags> = F extends LaunchDarklyFlag.ENABLE_ONE_TIME_PASSWORD
  ? OTPAuthDeliveryMethod
  : never;

export type BooleanFlags = Exclude<LaunchDarklyFlag, NumericFlags | StringFlags | EnumFlags>;

export type FlagType<F extends LaunchDarklyFlag> = F extends NumericFlags
  ? number
  : F extends StringFlags
  ? string
  : F extends BooleanFlags
  ? boolean
  : F extends EnumFlags
  ? EnumFlagTypes<F>
  : never;

export type LaunchDarklyFlagsObject = { [F in LaunchDarklyFlag]?: FlagType<F> };

export type UserAttributeUpdates = Partial<LDClient.LDUser & { cognitoId: string }>;

export type AnonymousUserAttributes = PromiseReturnType<typeof createAnonymousUserAttributes>;

export type UserAttributes = AnonymousUserAttributes & UserAttributeUpdates;

export default class LaunchDarklyHelper {
  private static instance: LaunchDarklyHelper;
  public launchDarkly: LDClient.LDClient;
  private _userAttributes: UserAttributes;

  public get userAttributes() {
    return this._userAttributes;
  }

  public async initAnonymousUserAttributes() {
    this._userAttributes = await createAnonymousUserAttributes();
    return this.userAttributes;
  }

  public async updateCurrentUser(changes: UserAttributeUpdates) {
    const normalized = normalizeUserAttributes(changes);
    const originalAttributes = this._userAttributes;
    const mergedAttributes = merge({}, originalAttributes, normalized);

    // Only update LD user if attributes are different via a deep comparison check
    let newFlags: LDClient.LDFlagSet | undefined;
    if (!isEqual(originalAttributes, mergedAttributes)) {
      this._userAttributes = mergedAttributes;
      const flags = await this.launchDarkly.identify(mergedAttributes);
      newFlags = LaunchDarklyHelper.flattenFlags(flags);
    }

    return { newFlags, userAttributes: mergedAttributes };
  }

  public clearCurrentUser = async () => {
    const originalAttributes = this._userAttributes;

    // Persist device id across user sessions
    const deviceId = originalAttributes?.custom?.device_id as string | undefined;
    const newAttributes = await createAnonymousUserAttributes(deviceId);

    const flags = await this.launchDarkly.identify(newAttributes);
    const newFlags = LaunchDarklyHelper.flattenFlags(flags);
    this._userAttributes = newAttributes;

    return { newFlags, userAttributes: newAttributes };
  };

  public static getInstance(): LaunchDarklyHelper {
    if (!LaunchDarklyHelper.instance) {
      LaunchDarklyHelper.instance = new LaunchDarklyHelper();
    }
    return LaunchDarklyHelper.instance;
  }

  public static flattenFlags(allFlags: LDFlagSet): LaunchDarklyFlagsObject {
    const flattened: LaunchDarklyFlagsObject = {};
    for (const key in allFlags) {
      flattened[key] = allFlags[key];
    }

    return flattened;
  }

  public static flattenChanges(changes: LDFlagChangeset): LaunchDarklyFlagsObject {
    const flattened = {};
    for (const key in changes) {
      flattened[key] = changes[key].current;
    }

    return flattened;
  }

  public static async init(bootstrapFlags?: object) {
    const helperInstance = this.getInstance();
    const anonUser = await helperInstance.initAnonymousUserAttributes();
    let config: LDClient.LDOptions = {
      privateAttributeNames: [
        'email',
        'firstName',
        'lastName',
        'name',
        'dateOfBirth',
        'phoneNumber'
      ],
      evaluationReasons: true,
      allowFrequentDuplicateEvents: true
    };
    if (bootstrapFlags) {
      config.bootstrap = bootstrapFlags;
    }
    const ldClient = LDClient.initialize(LAUNCH_DARKLY_API_KEY, anonUser, config);
    helperInstance.launchDarkly = ldClient;
    return ldClient;
  }
}

const trimStringKeys = <T extends {}>(attributes: T): T =>
  Object.entries(attributes).reduce((acc, [key, value]) => {
    if (typeof value === 'object') {
      return { ...acc, [key]: value && trimStringKeys(value) };
    }

    if (typeof value === 'string') {
      return { ...acc, [key]: value.trim() };
    }

    return { ...acc, [key]: value };
  }, {} as T);

const emailAttributeLowerCase = ({ email, ...attributes }: UserAttributeUpdates) => ({
  ...attributes,
  ...(email ? { email: email.toLowerCase() } : null)
});

export const normalizeUserAttributes = (userAttributeUpdates: UserAttributeUpdates) =>
  emailAttributeLowerCase(trimStringKeys(userAttributeUpdates));
